import React, { useState, useEffect } from "react";
import {
  createStyles,
  withStyles,
  WithStyles,
  StyleRules
} from "@material-ui/core/styles";
// ui
import Paper from "@material-ui/core/Paper";
import SectionTitle from "@components/atoms/SectionTitle";
import KnowbeButton from "@components/presentational/atoms/KnowbeButton";
import { DateSelectYearMonth } from "@components/molecules/DateSelectYearMonth";
import { RecordEachUser } from "@components/organisms/mgr/KEIKAKUSODAN/record/RecordEachUser";
// store
import { GetFacilityUsersResponse } from "@api/requests/facility/getFacilityUsers";
// utils
import { format } from "date-fns";
// variables
import { DEFAULT_SELECT_VALUE } from "@constants/variables";

const styles = (): StyleRules =>
  createStyles({
    paperContainer: {
      padding: "30px 32px 32px 32px",
      margin: 16
    },
    userList: {
      listStyle: "none",
      padding: 0,
      marginTop: 24,
      marginBottom: 0,
      borderTop: "1px solid #e5e5e5",
      borderBottom: "1px solid #e5e5e5"
    },
    noUser: {
      marginTop: 24,
      padding: "80px 0 48px",
      textAlign: "center",
      lineHeight: "28px",
      borderTop: "1px solid #e5e5e5",
      color: "rgba(0, 0, 0, 0.87)"
    },
    searchSection: {
      marginTop: "20px",
      backgroundColor: "#fafafa",
      borderRadius: 4,
      border: "solid 1px #bdbdbd",
      display: "flex",
      justifyContent: "space-between",
      padding: "20px 25px 15px"
    },
    searchButtonsWrap: { display: "flex", alignItems: "flex-end" },
    searchButton: {
      width: 168,
      minHeight: 30,
      marginRight: 8
    },
    clearButton: {
      display: "flex",
      padding: "3px 8px",
      width: 168,
      minHeight: 30
    }
  });

type OwnProps = {
  users: GetFacilityUsersResponse["data"];
  facilitySubType: number | null;
};

type Props = OwnProps & WithStyles<typeof styles>;

type DateValue = {
  year: string;
  month: string;
};

const RecordEachUserListCore = (props: Props): JSX.Element => {
  const { classes, users, facilitySubType } = props;

  const [targetUsers, setTargetUsers] = useState(users);
  const [targetMonth, setTargetMonth] = useState("");
  const [isDisabledSearchButton, setIsDisabledSearchButton] = useState(true);
  // 絞り込み状態のフラグ
  const [isSearch, setIsSearch] = useState(false);

  useEffect(() => {
    setTargetUsers(users);
    return (): void => {
      setIsSearch(false);
    };
  }, [users]);

  // 年月変更時
  const handleChangeMonthSelect = (value: DateValue): void => {
    if (value.year === DEFAULT_SELECT_VALUE || !value.month) {
      setIsDisabledSearchButton(true);
    } else {
      setTargetMonth(format(`${value.year}-${value.month}`, "YYYYMM"));
      setIsDisabledSearchButton(false);
    }
  };

  // 絞り込みボタン押下
  const handleSearchUsers = (): void => {
    const filteredUsers = users.filter(
      (user) =>
        user.monitoring_month && user.monitoring_month.includes(targetMonth)
    );
    setIsSearch(true);
    setTargetUsers(filteredUsers);
  };

  // 絞り込み解除ボタン押下
  const handleClear = (): void => {
    setTargetUsers(users);
    setIsSearch(false);
  };

  const noUserMessage = isSearch ? (
    <>
      条件に当てはまる利用者が見つかりませんでした。
      <br />
      条件を変更して再度絞り込みをしてください。
    </>
  ) : (
    <>利用者が登録されていません。利用者情報画面で登録後、ご利用ください。</>
  );

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <SectionTitle label="利用者ごとの記録" isTitleNoMargin />
      <div className={classes.searchSection}>
        <div>
          <DateSelectYearMonth
            label="モニタリング実施年月"
            overrideYearFrom={1989}
            addYearTo={1}
            onChangeSelect={handleChangeMonthSelect}
            defaultDate={{ year: DEFAULT_SELECT_VALUE, month: "" }}
          />
        </div>
        <div className={classes.searchButtonsWrap}>
          <KnowbeButton
            kind="outlineWhite"
            onClick={handleSearchUsers}
            className={classes.searchButton}
            disabled={isDisabledSearchButton}
          >
            この条件で絞り込む
          </KnowbeButton>
          <KnowbeButton
            className={classes.clearButton}
            onClick={handleClear}
            kind="outlineWhite"
            disabled={!isSearch}
          >
            絞り込みを解除する
          </KnowbeButton>
        </div>
      </div>
      {targetUsers.length > 0 ? (
        <ul className={classes.userList}>
          {targetUsers.map((user) => (
            <RecordEachUser
              key={user.uif_id}
              user={user}
              facilitySubType={facilitySubType}
            />
          ))}
        </ul>
      ) : (
        <div className={classes.noUser}>{noUserMessage}</div>
      )}
    </Paper>
  );
};

export const RecordEachUserList = withStyles(styles)(RecordEachUserListCore);
